import { Mail, Phone } from "lucide-react";

import { BrandIcon } from "@/components/shared/brand-icon";
import { Logo } from "@/components/shared/logo";
import { SITE } from "@/constants/site";
import { cn } from "@/lib/utils";

// =============================================================================
// Both faces of the business card (1005 × 650 in the design). Sizes use
// container query units so the card scales with its width.
// =============================================================================

const CARD_SURFACE =
  "absolute inset-0 overflow-hidden rounded-[3.6cqw] bg-[linear-gradient(135deg,#0c0c0c_0%,#161616_55%,#1e1e1e_100%)] text-white ring-1 ring-white/[0.08] [backface-visibility:hidden]";

// Film grain of the card
const GRAIN =
  "after:pointer-events-none after:absolute after:inset-0 after:opacity-[0.18] after:mix-blend-overlay after:bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")]";

type FaceProps = { className?: string };

type RectoProps = FaceProps & { role: string };

export function CardRecto({ role, className }: RectoProps) {
  return (
    <div className={cn(CARD_SURFACE, GRAIN, className)}>
      <div className="absolute top-[42%] left-[5.5%] text-center">
        <p className="text-[6.3cqw] leading-none font-extrabold tracking-[-0.02em]">
          {SITE.name}
        </p>
        <p className="mt-[2.2cqw] text-[2.5cqw] text-white/50">{role}</p>
      </div>
      {/* Silhouette (CSS mask so the color follows the card) */}
      <div
        aria-hidden
        className="absolute right-[5%] bottom-0 aspect-[442/532] w-[44%] bg-gradient-to-b from-[#353535] to-[#2a2a2a]"
        style={{
          maskImage: "url(/images/silhouette.png)",
          maskSize: "100% 100%",
          WebkitMaskImage: "url(/images/silhouette.png)",
          WebkitMaskSize: "100% 100%",
        }}
      />
    </div>
  );
}

type VersoProps = FaceProps & {
  tags: string[];
  /** SVG markup of the QR code (white modules on transparent) */
  qrSvg: string;
};

export function CardVerso({ tags, qrSvg, className }: VersoProps) {
  return (
    <div
      className={cn(
        CARD_SURFACE,
        GRAIN,
        "[transform:rotateY(180deg)]",
        className
      )}
    >
      {/* Logo hanging from the top edge (box of the design: 414, -20, 173) */}
      <div className="absolute top-[-3.05%] left-[41.21%] w-[17.26%]">
        <Logo className="w-full text-white" />
      </div>

      <div className="absolute top-[49.5%] left-1/2 flex -translate-x-1/2 gap-[2.2cqw] whitespace-nowrap">
        {tags.map((tag) => (
          <span
            key={tag}
            className="rounded-full border-[0.1cqw] border-white/25 px-[1.7cqw] py-[0.65cqw] text-[1.45cqw] font-semibold text-white/85"
          >
            {tag}
          </span>
        ))}
      </div>

      <div className="absolute inset-x-[10%] bottom-[10%] flex items-center">
        <div className="flex flex-1 justify-center">
          <div
            className="aspect-square w-[14.5cqw] [&_svg]:size-full"
            dangerouslySetInnerHTML={{ __html: qrSvg }}
          />
        </div>
        <span className="h-[16cqw] w-[0.1cqw] bg-white/15" />
        <ul className="flex flex-1 flex-col gap-[2cqw] pl-[13%] text-[1.65cqw] text-white/85">
          <li className="flex items-center gap-[1.6cqw]">
            <BrandIcon name="linkedin" className="size-[2.4cqw]" />
            {SITE.linkedinHandle}
          </li>
          <li className="flex items-center gap-[1.6cqw]">
            <Phone className="size-[2.2cqw]" strokeWidth={1.6} />
            {SITE.phone}
          </li>
          <li className="flex items-center gap-[1.6cqw]">
            <Mail className="size-[2.2cqw]" strokeWidth={1.6} />
            {SITE.email}
          </li>
        </ul>
      </div>
    </div>
  );
}

// =============================================================================
